import type { Liff } from '@line/liff';

// SDK は index.html で LINE の CDN（edge/2）から読み込み、window.liff を使う。
// バージョンを固定して同梱すると LINE 側の脆弱性修正に追従できないため（2026-10-29 KOTO 依頼。ポイントカードと同じ方式）。
// npm の @line/liff は型定義のためだけに使い、バンドルには含めない。
declare global {
  interface Window {
    liff?: Liff;
  }
}

function sdk(): Liff {
  if (!window.liff) throw new Error('LINEの読み込みに失敗しました。通信環境を確認して、もう一度開いてください。');
  return window.liff;
}

/**
 * LIFF SDK のラッパー。
 *
 * 方針（実現性検討メモ 2 章）:
 *   - 友だち登録は不要にする（LIFF アプリ単位で「友だち追加オプション」を off）
 *   - ログイン（本人確認）は入口で済ませ、ID トークンをサーバーへ渡す
 *   - userId はクライアントの値を信用せず、必ずサーバーで ID トークンを検証する
 *
 * 外部ブラウザから LIFF を起動できるかは端末 OS に依存するため、
 * LINE 内で開けていない場合は案内画面に倒す（実現性検討メモ R1）。
 */

export interface LiffState {
  ready: boolean;
  isInClient: boolean;
  isLoggedIn: boolean;
  idToken: string | null;
  error: string | null;
}

let initialized = false;

export async function initLiff(liffId: string): Promise<LiffState> {
  // LIFF ID が未設定の場合（ローカル開発）は SDK を使わずモックで進める
  if (!liffId) {
    return {
      ready: true,
      isInClient: false,
      isLoggedIn: true,
      idToken: import.meta.env.VITE_DEV_ID_TOKEN ?? 'dev-id-token',
      error: null,
    };
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const liff = sdk();
    if (!initialized) {
      // SDK 側の通信が終わらない場合も、読み込み中に留めず再起動できる案内へ戻す。
      await Promise.race([
        liff.init({ liffId }),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('LINEへの接続がタイムアウトしました')), 30000);
        }),
      ]);
      initialized = true;
    }

    if (!liff.isLoggedIn()) {
      // 未ログインならログインへ。戻り先は現在の URL（?es= を保持する）
      liff.login({ redirectUri: window.location.href });
      return { ready: false, isInClient: liff.isInClient(), isLoggedIn: false, idToken: null, error: null };
    }

    return {
      ready: true,
      isInClient: liff.isInClient(),
      isLoggedIn: true,
      idToken: liff.getIDToken(),
      error: null,
    };
  } catch (e) {
    return {
      ready: true,
      isInClient: false,
      isLoggedIn: false,
      idToken: null,
      error: e instanceof Error ? e.message : 'LIFF の初期化に失敗しました',
    };
  } finally {
    clearTimeout(timer);
  }
}

/** 友だち確認用。期限更新に追随するため、呼出しのたびに SDK から現在の値を取得する */
export function getAccessToken(): string | null {
  return initialized ? sdk().getAccessToken() : null;
}

/** 外部ブラウザは再ログイン、LIFF 内は再初期化。タッチ ID を含む現在の URL を保持する */
export function reauthenticate(): void {
  if (initialized && !sdk().isInClient() && sdk().isLoggedIn()) sdk().logout();
  window.location.reload();
}

/** 公式アカウントの友だち追加画面を開く */
export function openAddFriend(addFriendUrl: string): void {
  if (!addFriendUrl) return;
  if (initialized && sdk().isInClient()) {
    sdk().openWindow({ url: addFriendUrl, external: false });
  } else {
    window.location.href = addFriendUrl;
  }
}

/** 特典を確認できる外部アプリ（初期リリースではバリアンアプリ）を開く */
export function openExternal(url: string): void {
  if (initialized && sdk().isInClient()) {
    sdk().openWindow({ url, external: false });
  } else {
    window.open(url, '_blank', 'noopener');
  }
}
